import { useState, useMemo, type ChangeEvent } from "react";

import "./starter-pay.css";

const isGetSnugDomain =
  window.location.hostname.endsWith("getsnug.com") ||
  window.location.hostname === "localhost";

const BASE_URL = "https://dashboard.getsnug.com/get-started-account";

function CopyIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect
        x="5"
        y="5"
        width="8"
        height="9"
        rx="1.5"
        stroke="currentColor"
        strokeWidth="1.5"
      />
      <path
        d="M3 10V3.5A1.5 1.5 0 014.5 2H10"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path
        d="M3 8.5l3 3 7-7"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function GetStartedLinkBuilderPage() {
  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [email, setEmail] = useState("");
  const [demoDate, setDemoDate] = useState("");
  const [copied, setCopied] = useState(false);

  const shareUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (name.trim()) params.set("name", name.trim());
    if (email.trim()) params.set("email", email.trim());
    if (company.trim()) params.set("company", company.trim());
    if (demoDate.trim()) params.set("demo_date", demoDate.trim());
    const qs = params.toString();
    return qs ? `${BASE_URL}?${qs}` : BASE_URL;
  }, [name, email, company, demoDate]);

  if (!isGetSnugDomain) return null;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for browsers without clipboard API
      const ta = document.createElement("textarea");
      ta.value = shareUrl;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleChange =
    (setter: (v: string) => void) => (e: ChangeEvent<HTMLInputElement>) => {
      setter(e.target.value);
    };

  return (
    <div className="sp-page">
      <div className="sp-brand">
        <img
          src="https://s3.us-east-2.amazonaws.com/assets.getsnug.com/files/snug_logo.png"
          alt="Snug"
          className="sp-brand-logo"
        />
      </div>

      <section className="sp-inner">
        <h1 className="sp-headline">
          Get started <em>link builder.</em>
        </h1>

        <p className="sp-sub">
          Fill in the prospect's details to generate a prefilled signup link you
          can share.
        </p>

        <div className="sp-card">
          <div className="sp-card-head">
            <h2 className="sp-section-title">Prospect details</h2>
            <p className="sp-section-help">
              All fields are optional — include whatever you have. The link will
              prefill the matching fields on the signup page.
            </p>
          </div>
          <div className="sp-input-grid sp-input-grid-1">
            <label className="sp-input">
              <span className="sp-input-label">Full name</span>
              <input
                type="text"
                className="sp-input-field"
                value={name}
                onChange={handleChange(setName)}
                placeholder="Jane Smith"
                autoFocus
                autoComplete="off"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Firm name</span>
              <input
                type="text"
                className="sp-input-field"
                value={company}
                onChange={handleChange(setCompany)}
                placeholder="Smith Financial"
                autoComplete="off"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Email</span>
              <input
                type="email"
                className="sp-input-field"
                value={email}
                onChange={handleChange(setEmail)}
                placeholder="jane@smithfinancial.com"
                autoComplete="off"
              />
            </label>
            <label className="sp-input">
              <span className="sp-input-label">Demo date</span>
              <input
                type="date"
                className="sp-input-field"
                value={demoDate}
                onChange={handleChange(setDemoDate)}
                autoComplete="off"
              />
            </label>
          </div>

          <div style={{ marginTop: 24 }}>
            <span className="sp-input-label">Shareable link</span>
            <div
              style={{
                marginTop: 8,
                padding: 12,
                background: "#f6f7f9",
                border: "1px solid #e5e7eb",
                borderRadius: 8,
                fontFamily:
                  "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                fontSize: 13,
                wordBreak: "break-all",
                color: "#111827",
              }}
            >
              {shareUrl}
            </div>
          </div>
        </div>

        <button
          type="button"
          className="sp-cta"
          onClick={handleCopy}
          style={{ background: copied ? "#16a34a" : undefined }}
        >
          {copied ? <CheckIcon /> : <CopyIcon />}
          {copied ? "Copied to clipboard" : "Copy link"}
        </button>

        <p className="sp-disclaimer">
          The link points to{" "}
          <strong>dashboard.getsnug.com/get-started-account</strong> and uses
          standard URL encoding for special characters.
        </p>
      </section>
    </div>
  );
}
